import React, { useEffect, useState } from "react";
import { Layers, Users, ArrowRight, X, Copy, Download, Loader2, Star, Flame } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

// Lists a user's saved Engine outputs (Blueprint/Software/Marketing/Consulting) as clickable cards.
// Others open them for 5 credits (owner earns 15%); owner opens free.
export default function UserOutputs({ ownerId, isOwner = false, category = "", title = "Saved outputs — Save & Earn" }) {
  const { user, setUser } = useApp();
  const [outputs, setOutputs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [opened, setOpened] = useState(null);
  const [dl, setDl] = useState("");
  const [rateBusy, setRateBusy] = useState(false);

  const OutStars = ({ value = 0, size = 12 }) => (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} size={size} className={n <= Math.round(value) ? "text-brand" : "text-sec"} style={{ fill: n <= Math.round(value) ? "currentColor" : "none" }} />)}
    </span>
  );
  const rate = async (n) => {
    if (!user) { toast.error("Sign in to rate."); return; }
    setRateBusy(true);
    try {
      const { data } = await api.post(`/outputs/${opened.output_id || opened.id}/rate`, { rating: n });
      setOpened((o) => ({ ...o, rating_avg: data.rating_avg, rating_count: data.rating_count, my_rating: n }));
      toast.success("Thanks for rating!"); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRateBusy(false); }
  };

  const load = () => {
    if (!ownerId) return;
    api.get("/outputs", { params: { owner: ownerId, limit: 12, ...(category ? { category } : {}) } })
      .then(({ data }) => setOutputs(data.outputs || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  };
  useEffect(load, [ownerId, category]);

  const open = async (o) => {
    if (!user) { toast.error("Sign in to open saved outputs."); return; }
    setBusyId(o.id);
    try {
      const { data } = await api.get(`/outputs/${o.id}`);
      setOpened({ ...o, ...data });
      if (data.charged && typeof data.credits_remaining === "number" && setUser) setUser((u) => u ? { ...u, credits: data.credits_remaining } : u);
      if (data.charged) toast.success(`Opened · ${data.charged} credits · creator earned ${data.creator_earned}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusyId(""); }
  };

  const copy = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } };
  const downloadMd = (o) => {
    const blob = new Blob([o.text || ""], { type: "text/markdown" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `${(o.title || "output").slice(0, 50)}.md`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  const exportFile = async (o, fmt) => {
    setDl(fmt);
    try {
      const { data } = await api.post("/export", {
        title: o.title, deliverable_label: o.deliverable_label || o.solution_type, region: o.region || "",
        content: o.content || {}, format: fmt, site_url: window.location.origin,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data])); const a = document.createElement("a");
      a.href = url; a.download = `${(o.title || "output").slice(0, 40)}.${fmt}`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not export this file."); }
    finally { setDl(""); }
  };

  if (!loaded) return null;
  if (outputs.length === 0) {
    if (!isOwner) return null;
    return (
      <div className="rud-card rounded-2xl p-6" data-testid="user-outputs-empty">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><Layers size={14} /> {title}</div>
        <p className="text-sm text-sec">No saved outputs yet. In the Engine, generate a Blueprint, Software, Marketing or Consulting output and hit <b>Save &amp; Earn</b> — earn 15% every time someone opens it.</p>
      </div>
    );
  }

  return (
    <div className="rud-card rounded-2xl p-6" data-testid="user-outputs">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><Layers size={14} /> {title}</div>
      <p className="text-xs text-sec mb-4">
        {isOwner
          ? "Outputs you saved. Anyone who opens one spends 5 credits and you earn 15%."
          : "Open a ready-made output — costs you 5 credits, and the creator earns 15%."}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {outputs.map((o) => (
          <button key={o.id} data-testid={`user-output-${o.id}`} onClick={() => open(o)} disabled={busyId === o.id}
            className="text-left rounded-xl border border-rud bg-elevated p-4 hover:border-brand/50 transition-colors group disabled:opacity-60">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-brand border border-brand/40 rounded-full px-2 py-0.5">{o.deliverable_label || o.solution_type}</span>
              {o.trending && <span data-testid={`trending-${o.id}`} className="text-[10px] uppercase tracking-widest text-black bg-brand rounded-full px-2 py-0.5 flex items-center gap-1"><Flame size={10} /> Trending</span>}
              {o.industry && <span className="text-[11px] text-sec truncate">{o.industry}</span>}
            </div>
            <div className="font-medium text-sm mt-2 line-clamp-2 group-hover:text-brand transition-colors">{o.title}</div>
            {o.snippet && <p className="text-xs text-sec mt-1 line-clamp-2">{o.snippet}</p>}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-rud text-[11px] text-sec">
              <span className="flex items-center gap-1"><Users size={11} /> {o.uses} uses</span>
              {o.rating_count > 0 && <span className="flex items-center gap-1" data-testid={`rating-${o.id}`}><OutStars value={o.rating_avg} size={11} /> {o.rating_avg}</span>}
              {isOwner && <span className="text-brand">earned {o.earned} cr</span>}
              <span className="ml-auto flex items-center gap-1 text-brand">
                {busyId === o.id ? <Loader2 size={11} className="animate-spin" /> : <>{isOwner ? "Open" : "Open · 5 cr"} <ArrowRight size={11} /></>}
              </span>
            </div>
          </button>
        ))}
      </div>

      {opened && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" onClick={() => setOpened(null)} data-testid="output-modal">
          <div className="rud-card rounded-2xl p-6 max-w-2xl w-full max-h-[82vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand">{opened.deliverable_label || opened.solution_type}</div>
                <h3 className="font-serif text-2xl">{opened.title}</h3>
              </div>
              <button onClick={() => setOpened(null)} className="text-sec hover:text-brand" data-testid="output-modal-close"><X size={20} /></button>
            </div>
            <pre className="whitespace-pre-wrap text-sm mt-4 bg-elevated border border-rud rounded-lg p-4 font-sans leading-relaxed">{opened.text}</pre>
            <div className="flex items-center gap-3 mt-4 pt-3 border-t border-rud flex-wrap" data-testid="output-rate">
              <span className="text-xs text-sec">{opened.rating_count > 0 ? `${opened.rating_avg}★ · ${opened.rating_count} rating${opened.rating_count > 1 ? "s" : ""}` : "Be the first to rate"}</span>
              {!opened.is_owner && (
                <span className="flex items-center gap-1">
                  <span className="text-xs text-sec mr-1">Rate:</span>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`output-rate-${n}`} disabled={rateBusy} onClick={() => rate(n)} className="p-0.5">
                      <Star size={18} className={n <= (opened.my_rating || 0) ? "text-brand" : "text-sec hover:text-brand"} style={{ fill: n <= (opened.my_rating || 0) ? "currentColor" : "none" }} />
                    </button>
                  ))}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <button data-testid="output-copy" onClick={() => copy(opened.text)} className="btn-primary h-10 px-5 text-sm flex items-center gap-2"><Copy size={15} /> Copy</button>
              <button data-testid="output-dl-md" onClick={() => downloadMd(opened)} className="btn-ghost rounded-full h-10 px-5 text-sm flex items-center gap-2"><Download size={15} /> .md</button>
              {["pdf", "docx"].map((f) => (
                <button key={f} data-testid={`output-dl-${f}`} onClick={() => exportFile(opened, f)} disabled={!!dl} className="btn-ghost rounded-full h-10 px-5 text-sm flex items-center gap-2 uppercase disabled:opacity-50">
                  {dl === f ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {f}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
